import { isNullOrUndefined, createElement, remove, extend, EventHandler } from '@syncfusion/ej2-base';
import * as cls from '../base/css-constants';
import { CalendarContext } from '../base/calendar-context';
import { EditTooltip } from '../renderer/edit-tooltip';
var TaskbarEditDraw = /** @class */ (function () {
    function TaskbarEditDraw(ganttObj) {
        this.isDrawing = false;
        this.isMouseDragged = false;
        this.isDrawCancel = false;
        this.mouseDownX = 0;
        this.mouseDownY = 0;
        this.mouseMoveX = 0;
        this.startRowIndex = -1;
        this.startDate = null;
        this.endDate = null;
        this.startLeft = 0;
        this.endLeft = 0;
        this.previewTaskbar = null;
        this.previewContainer = null;
        this.scrollTimer = null;
        this.scrollDirection = null;
        this.editTooltip = null;
        this.dragMouseLeave = false;
        this.minDrawWidth = 1; // 1 day minimum
        this.dragThreshold = 3; // pixels
        this.parent = ganttObj;
        // Create a mock TaskbarEdit object for the tooltip
        var mockTaskbarEdit = {
            taskBarEditRecord: null,
            taskBarEditAction: 'ChildDrag',
            tooltipPositionX: 0
        };
        this.editTooltip = new EditTooltip(this.parent, mockTaskbarEdit);
        this.wireEvents();
    }
    TaskbarEditDraw.prototype.wireEvents = function () {
        this.parent.on('chartMouseDown', this.mouseDownHandler, this);
        this.parent.on('chartMouseUp', this.mouseUpHandler, this);
        this.parent.on('chartMouseLeave', this.mouseLeaveHandler, this);
        this.parent.on('chartMouseMove', this.mouseMoveHandler, this);
        EventHandler.add(document.body, 'keydown', this.keyDownHandler, this);
    };
    TaskbarEditDraw.prototype.unWireEvents = function () {
        this.parent.off('chartMouseDown', this.mouseDownHandler);
        this.parent.off('chartMouseUp', this.mouseUpHandler);
        this.parent.off('chartMouseLeave', this.mouseLeaveHandler);
        this.parent.off('chartMouseMove', this.mouseMoveHandler);
        EventHandler.remove(document.body, 'keydown', this.keyDownHandler);
    };
    /**
     * Resolves the row index from the pointer's clientY by walking the
     * rendered chart rows.
     *
     * @param {boolean} clientY .
     * @returns {number} - Returns the corresponding row index.
     */
    TaskbarEditDraw.prototype.getRowIndexFromY = function (clientY) {
        var rows = this.parent.ganttChartModule.getChartRows();
        if (!rows || rows.length === 0) {
            return -1;
        }
        else {
            for (var i = 0; i < rows.length; i++) {
                var rect = rows[i].getBoundingClientRect();
                if (clientY >= rect.top && clientY <= rect.bottom) {
                    var rowIndex = parseInt(rows[i].getAttribute('aria-rowindex'), 10);
                    return (rowIndex - 1);
                }
            }
            return -1;
        }
    };
    /**
     * Gets the rendered chart row element for the specified absolute row index.
     *
     * @param {number} rowIndex - Defines the absolute row index.
     * @returns {HTMLElement} - Returns the matching row element, or `null` if not currently rendered.
     */
    TaskbarEditDraw.prototype.getDrawRowElement = function (rowIndex) {
        if (rowIndex < 0) {
            return null;
        }
        else if (!this.parent.enableVirtualization) {
            return this.parent.getRowByIndex(rowIndex);
        }
        else {
            var rows = this.parent.ganttChartModule.getChartRows();
            for (var i = 0; i < rows.length; i++) {
                if (parseInt(rows[i].getAttribute('aria-rowindex'), 10) - 1 === rowIndex) {
                    return rows[i];
                }
            }
            return null;
        }
    };
    /**
     * Returns true if the row at `rowIndex` is a parent row (has child
     * records or treegrid expand icon). Drawing inside parents is
     * disallowed to preserve parent-child calculations.
     *
     * @param {number} rowIndex - Defines the row index.
     * @returns {boolean} - Returns `true` if the row at `rowIndex` is a parent row; otherwise, `false`.
     */
    TaskbarEditDraw.prototype.isParentRow = function (rowIndex) {
        if (rowIndex < 0 || rowIndex >= this.parent.currentViewData.length) {
            return false;
        }
        else {
            var data = this.parent.currentViewData[rowIndex];
            if (data && data.hasChildRecords) {
                return true;
            }
            else {
                var tr = this.getDrawRowElement(rowIndex);
                if (tr && tr.querySelector('.e-treegridexpand')) {
                    return true;
                }
                else {
                    return false;
                }
            }
        }
    };
    TaskbarEditDraw.prototype.isExistingTaskbarClicked = function (e) {
        if (this.parent.editModule && this.parent.editModule.taskbarEditModule) {
            var action = this.parent.editModule.taskbarEditModule['getTaskBarAction'](e);
            return !!(action !== '');
        }
        return false;
    };
    TaskbarEditDraw.prototype.mouseDownHandler = function (e) {
        if (!this.parent.editSettings.allowTaskbarDraw
            || !this.parent.allowUnscheduledTasks
            || this.parent.readOnly
            || this.parent.isAdaptive
            || this.isDrawing) {
            return;
        }
        else {
            if (this.isExistingTaskbarClicked(e)) {
                return;
            }
            var rowIndex = this.getRowIndexFromY(e.clientY);
            if (rowIndex < 0) {
                return; // no row under the pointer
            }
            else {
                this.startRowIndex = rowIndex;
                var containerPos = this.parent.getOffsetRect(this.parent.ganttChartModule.chartBodyContainer);
                var coord = this.getCoordinate(e);
                if (this.parent.enableRtl) {
                    this.mouseDownX = Math.abs(coord.pageX -
                        (containerPos.left +
                            Math.abs(this.parent.ganttChartModule.scrollObject.previousScroll.left)));
                }
                else {
                    this.mouseDownX = (coord.pageX - containerPos.left) +
                        this.parent.ganttChartModule.scrollObject.previousScroll.left;
                }
                this.mouseDownY = (coord.pageY - containerPos.top) +
                    this.parent.ganttChartModule.scrollObject.previousScroll.top;
                this.isMouseDragged = false;
                this.dragMouseLeave = false;
                e.preventDefault();
            }
        }
    };
    TaskbarEditDraw.prototype.mouseMoveHandler = function (e) {
        if (this.startRowIndex < 0) {
            return;
        }
        else {
            var rowIndex = this.getRowIndexFromY(e.clientY);
            if (rowIndex >= 0 && rowIndex === this.startRowIndex) {
                var tr_1 = this.getDrawRowElement(rowIndex);
                if (tr_1) {
                    if (this.isParentRow(rowIndex)) {
                        tr_1.style.cursor = 'not-allowed'; // parent row
                    }
                    else {
                        // Check if the row already has a rendered taskbar
                        var taskbarContainer = tr_1.querySelector('.' + cls.traceChildTaskBar);
                        var milestoneContainer = tr_1.querySelector('.' + cls.traceMilestone);
                        var milestoneParentContainer = tr_1.querySelector('.' + cls.parentMilestone);
                        if (taskbarContainer || milestoneContainer || milestoneParentContainer) {
                            tr_1.style.cursor = 'not-allowed'; // taskbar already rendered
                        }
                        else {
                            tr_1.style.cursor = ''; // eligible for drawing
                        }
                    }
                    if (tr_1.style.cursor === 'not-allowed') {
                        this.isDrawing = false;
                        return;
                    }
                    else {
                        this.isDrawing = true;
                    }
                }
            }
            else {
                this.isDrawing = false;
                return;
            }
            this.dragMouseLeave = false;
            var tr = this.getDrawRowElement(this.startRowIndex);
            if (tr) {
                tr.style.cursor = 'crosshair';
            }
            if (!this.isMouseDragged) {
                var deltaX = Math.abs(e.pageX - this.mouseDownX);
                if (deltaX < this.dragThreshold) {
                    return;
                }
                this.isMouseDragged = true;
                var rowData = this.parent.currentViewData[rowIndex];
                var eventArgs = {
                    rowData: rowData,
                    requestType: 'taskbarDraw',
                    cancel: false
                };
                // eslint-disable-next-line
                this.parent.trigger('actionBegin', eventArgs, this.handleActionBegin.bind(this));
            }
            if (!this.isDrawCancel) {
                var containerPos = this.parent.getOffsetRect(this.parent.ganttChartModule.chartBodyContainer);
                var coord = this.getCoordinate(e);
                if (this.parent.enableRtl) {
                    this.mouseMoveX = Math.abs(coord.pageX -
                        (containerPos.left +
                            Math.abs(this.parent.ganttChartModule.scrollObject.previousScroll.left)));
                }
                else {
                    this.mouseMoveX = (coord.pageX - containerPos.left) +
                        this.parent.ganttChartModule.scrollObject.previousScroll.left;
                }
                this.updateDrawPreview();
                this.handleAutoScroll(e);
            }
        }
    };
    TaskbarEditDraw.prototype.handleActionBegin = function (args) {
        if (!args.cancel) {
            this.isDrawCancel = args.cancel;
            this.startDrawPreview();
        }
        else {
            this.isDrawCancel = args.cancel;
            this.cleanupPreview();
            this.stopScrollTimer();
            this.reset();
            return;
        }
    };
    TaskbarEditDraw.prototype.mouseUpHandler = function (e) {
        if (this.startRowIndex >= 0) {
            var tr = this.getDrawRowElement(this.startRowIndex);
            if (tr) {
                tr.style.cursor = '';
            }
        }
        if (!this.isDrawing || this.dragMouseLeave) {
            this.cleanupPreview();
            this.stopScrollTimer();
            this.reset();
            return;
        }
        else {
            var startRow = this.startRowIndex;
            var startD = this.startDate;
            var endD = this.endDate;
            this.cleanupPreview();
            this.stopScrollTimer();
            var existingRecord = this.parent.flatData[startRow];
            var updatedRecord = extend({}, {}, existingRecord.taskData, true);
            var startField = this.parent.taskFields.startDate;
            var durationField = this.parent.taskFields.duration;
            updatedRecord[startField] = startD;
            var calendarContext = new CalendarContext(this.parent, this.parent.calendarSettings.projectCalendar);
            var duration = this.parent.dateValidationModule.getDuration(startD, endD, this.parent.durationUnit.toLocaleLowerCase(), true, false, undefined, calendarContext);
            updatedRecord[durationField] = duration;
            this.parent.editModule.updateRecordByID(updatedRecord);
            this.parent.selectionModule.selectRow(startRow);
            this.reset();
        }
    };
    TaskbarEditDraw.prototype.mouseLeaveHandler = function (e) {
        if (!this.isDrawing) {
            return;
        }
        else {
            this.dragMouseLeave = true;
            var tr = this.getDrawRowElement(this.startRowIndex);
            if (tr) {
                tr.style.cursor = '';
            }
            this.stopScrollTimer();
        }
    };
    TaskbarEditDraw.prototype.keyDownHandler = function (e) {
        if (e.key === 'Escape' && this.isDrawing) {
            e.preventDefault();
            if (this.startRowIndex >= 0) {
                var tr = this.getDrawRowElement(this.startRowIndex);
                if (tr) {
                    tr.style.cursor = '';
                }
            }
            this.cleanupPreview();
            this.stopScrollTimer();
            this.reset();
        }
    };
    TaskbarEditDraw.prototype.startDrawPreview = function () {
        var data = this.parent.flatData[this.startRowIndex];
        var isManual = !isNullOrUndefined(this.parent.taskFields.manual) ? data[this.parent.taskFields.manual] : false;
        var taskbarHeight = !isNullOrUndefined(this.parent.taskbarHeight) ?
            this.parent.taskbarHeight : (this.parent.rowHeight * 0.62);
        var container = this.parent.ganttChartModule.chartBodyContainer;
        this.previewContainer = createElement('div', {
            className: cls.drawPreviewTaskbar,
            styles: 'position: absolute; pointer-events: none; z-index: 4;'
        });
        this.previewTaskbar = createElement('div', {
            className: cls.taskBarMainContainer,
            styles: 'position: absolute; height: ' +
                taskbarHeight + 'px; top: 0;'
        });
        var childTaskbarInner = createElement('div', {
            className: (isManual ? cls.manualChildTaskBar : cls.traceChildTaskBar) + ' e-gantt-child-taskbar-inner-div',
            styles: 'height: 100%; width: 100%; position: relative;'
        });
        this.previewTaskbar.appendChild(childTaskbarInner);
        var childProgressBar = createElement('div', {
            className: (isManual ? cls.manualChildProgressBar : cls.traceChildProgressBar) + ' e-gantt-child-progressbar-inner-div',
            styles: 'height: 100%; width: 0%; position: absolute;'
        });
        childTaskbarInner.appendChild(childProgressBar);
        this.previewContainer.appendChild(this.previewTaskbar);
        var rowsContainer = container.querySelector('.' + cls.chartBodyContent);
        if (rowsContainer) {
            rowsContainer.appendChild(this.previewContainer);
        }
        else {
            container.appendChild(this.previewContainer);
        }
        if (this.parent.tooltipSettings.showTooltip && !isNullOrUndefined(this.editTooltip)) {
            this.editTooltip['taskbarEdit'].taskBarEditElement = this.previewTaskbar;
            this.editTooltip.showHideTaskbarEditTooltip(true, -1);
        }
    };
    TaskbarEditDraw.prototype.updateDrawPreview = function () {
        if (!this.previewTaskbar || !this.parent.editModule.taskbarEditModule) {
            return;
        }
        var isDraggingForward = this.mouseMoveX >= this.mouseDownX;
        var startX = isDraggingForward ? this.mouseDownX : this.mouseMoveX;
        var endX = isDraggingForward ? this.mouseMoveX : this.mouseDownX;
        var dateUtil = this.parent.editModule.taskbarEditModule.getDateByLeft.bind(this.parent.editModule.taskbarEditModule);
        var actualStartLeft = startX;
        var actualEndLeft = endX;
        var width = actualEndLeft - actualStartLeft;
        var tr = this.getDrawRowElement(this.startRowIndex);
        var rowTop = 0;
        if (tr) {
            rowTop = tr.offsetTop + ((this.parent.rowHeight - (this.parent.rowHeight * 0.62)) / 2);
        }
        if (this.parent.enableRtl) {
            this.previewTaskbar.style.right = actualStartLeft + 'px';
        }
        else {
            this.previewTaskbar.style.left = actualStartLeft + 'px';
        }
        this.previewTaskbar.style.width = width + 'px';
        this.previewTaskbar.style.top = rowTop + 'px';
        this.previewTaskbar.style.opacity = '0.75';
        var startTaskData = { startDate: dateUtil(actualStartLeft), left: actualStartLeft };
        var startLeft = this.parent.editModule.taskbarEditModule.getRoundOffStartLeft(startTaskData, true);
        var finalWidth = actualEndLeft - startLeft;
        var finalStartDate = dateUtil(startLeft);
        var finalEndDate = dateUtil(actualEndLeft);
        this.startDate = finalStartDate;
        this.endDate = finalEndDate;
        this.startLeft = actualStartLeft;
        this.endLeft = actualEndLeft;
        if (this.parent.tooltipSettings.showTooltip) {
            var mockTaskRecord = {
                ganttProperties: {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    duration: this.computeDuration(this.startDate, this.endDate),
                    width: finalWidth,
                    left: startLeft
                }
            };
            this.editTooltip.taskbarEdit.taskBarEditRecord = mockTaskRecord;
            this.editTooltip.taskbarEdit.taskBarEditAction = 'ChildDrag';
            var forward = this.parent.enableRtl ? !isDraggingForward : isDraggingForward;
            this.editTooltip['taskbarEdit'].tooltipPositionX = forward ? this.endLeft : this.startLeft;
            this.editTooltip.updateTooltip(-1);
        }
    };
    TaskbarEditDraw.prototype.cleanupPreview = function () {
        if (this.previewContainer && this.previewContainer.parentElement) {
            remove(this.previewContainer);
        }
        this.previewContainer = null;
        this.previewTaskbar = null;
        if (this.editTooltip) {
            this.editTooltip['taskbarEdit'].taskBarEditElement = null;
            this.editTooltip.showHideTaskbarEditTooltip(false, -1);
        }
        else {
            return;
        }
    };
    /**
     * Returns true if a draw is currently in progress.
     * Read by `TaskbarEdit.mouseDownHandler` to yield control.
     *
     * @returns {boolean} - Returns `true` if taskbar drawing is in progress; otherwise, `false`.
     */
    TaskbarEditDraw.prototype.getIsDrawing = function () {
        return this.isDrawing;
    };
    TaskbarEditDraw.prototype.reset = function () {
        this.isDrawing = false;
        this.isMouseDragged = false;
        this.dragMouseLeave = false;
        this.startRowIndex = -1;
        this.startDate = null;
        this.endDate = null;
        this.startLeft = 0;
        this.endLeft = 0;
        this.mouseDownX = 0;
        this.mouseDownY = 0;
        this.mouseMoveX = 0;
    };
    TaskbarEditDraw.prototype.handleAutoScroll = function (e) {
        var chart = this.parent.ganttChartModule.chartElement;
        var rect = chart.getBoundingClientRect();
        var edge = 30; // px from edge to start auto-scroll
        var direction = null;
        if (e.clientX > rect.right - edge) {
            direction = 'right';
        }
        else if (e.clientX < rect.left + edge) {
            direction = 'left';
        }
        if (direction) {
            if (!this.scrollTimer || this.scrollDirection !== direction) {
                this.stopScrollTimer();
                this.startScrollTimer(direction);
            }
        }
        else {
            this.stopScrollTimer();
        }
    };
    TaskbarEditDraw.prototype.processAutoScroll = function (direction) {
        if (!this.parent.ganttChartModule || !this.parent.ganttChartModule.scrollObject) {
            return;
        }
        else {
            if (direction === 'right') {
                this.parent.ganttChartModule.scrollObject.setScrollLeft(this.parent.ganttChartModule.scrollObject.previousScroll.left + 1);
                if (this.parent.enableRtl) {
                    this.mouseMoveX = this.mouseMoveX - 1;
                }
                else {
                    this.mouseMoveX = this.mouseMoveX + 1;
                }
            }
            else {
                this.parent.ganttChartModule.scrollObject.setScrollLeft(this.parent.ganttChartModule.scrollObject.previousScroll.left - 1);
                if (this.parent.enableRtl) {
                    this.mouseMoveX = this.mouseMoveX + 1;
                }
                else {
                    this.mouseMoveX = this.mouseMoveX - 1;
                }
            }
            this.updateDrawPreview();
        }
    };
    TaskbarEditDraw.prototype.startScrollTimer = function (direction) {
        var _this = this;
        this.scrollDirection = direction;
        this.stopScrollTimer();
        this.scrollTimer = window.setInterval(function () { _this.processAutoScroll(direction); }, 0);
    };
    TaskbarEditDraw.prototype.stopScrollTimer = function () {
        if (this.scrollTimer) {
            clearInterval(this.scrollTimer);
            this.scrollTimer = null;
        }
        else {
            return;
        }
    };
    TaskbarEditDraw.prototype.getCoordinate = function (e) {
        if (e.touches && e.touches.length) {
            var t = e.touches[0];
            return { pageX: t.pageX, pageY: t.pageY };
        }
        else {
            return { pageX: e.pageX, pageY: e.pageY };
        }
    };
    TaskbarEditDraw.prototype.computeDuration = function (start, end) {
        if (!start || !end) {
            return 0;
        }
        else {
            var calendarContext = new CalendarContext(this.parent, this.parent.calendarSettings.projectCalendar);
            return this.parent.dateValidationModule.getDuration(start, end, this.parent.durationUnit.toLocaleLowerCase(), true, false, undefined, calendarContext);
        }
    };
    TaskbarEditDraw.prototype.destroy = function () {
        this.unWireEvents();
        this.cleanupPreview();
        this.stopScrollTimer();
        this.reset();
        if (this.editTooltip) {
            this.editTooltip = null;
        }
    };
    return TaskbarEditDraw;
}());
export { TaskbarEditDraw };
